import { __ } from '@wordpress/i18n';
import { registerPaymentMethod, registerExpressPaymentMethod } from '@woocommerce/blocks-registry';
import { getSetting } from '@woocommerce/settings';
import { useEffect } from '@wordpress/element'
import {
    handle_paysafe_js_setup_error,
    get_paysafe_label,
    get_paysafe_express_label,
    set_log_nonce,
    handle_express_payment_click,
    main_paysafe_checkout_success_handler,
    resolve_paysafe_payment_description,
    resolve_paysafe_express_payment_description,
    init_paysafe_fields
} from "./paysafe-common";

const paymentMethodId = "google_pay";
const settings = getSetting( 'google_pay_data', {} );
set_log_nonce(settings.nonce);
const is_hosted_integration = settings.integration_type === 'paysafe_js';

let saveListenerControllerGooglePay;

let is_apple_pay_google_pay_combo_activated = false;
if (!!settings.is_apple_pay_express_enabled && !!settings.is_google_pay_express_enabled) {
    is_apple_pay_google_pay_combo_activated = true;
}
let canExpressPayWithGooglePay = true;

let current_cart_data = {};

let is_express_google_pay_initialized = false;
const init_google_pay_hosted_integration = function(onClick, onClose, onError) {
    // if the page doesn't have the form, don't load it
    if (!jQuery('#paysafe-hosted-google-pay-payment-form').length) {
        return;
    }

    if (!!settings.is_apple_pay_express_enabled && !!settings.is_google_pay_express_enabled) {
        return;
    }

    if (is_express_google_pay_initialized) {
        return;
    }

    if (typeof(paysafe) === "undefined" || typeof(paysafe.fields) === "undefined") {
        setTimeout(function() {
            init_google_pay_hosted_integration(onClick, onClose, onError);
        }, 1000);
        return;
    }

    is_express_google_pay_initialized = true;

    let paysafeOptions = {
        // You must provide currencyCode to the Paysafe JS SDK to enable the Payment API integration
        currencyCode: settings.currency_code,

        // select the Paysafe test / sandbox environment
        environment: settings.test_mode ? 'TEST' : 'LIVE',

        transactionSource: 'WooCommerceJs',

        accounts: {
            default: settings.account_id,
            googlePay: settings.account_id,
        },

        fields: {
            googlePay: {
                selector: '#paysafe-google-pay',
                type: 'buy',
                color: 'black',
                label: __('Google Pay', 'paysafe-checkout'),
            },
        },
    };
    if (settings.google_pay_issuer_country) {
        paysafeOptions.fields.googlePay.country = settings.google_pay_issuer_country;
    }
    if (settings.google_pay_merchant_id) {
        paysafeOptions.fields.googlePay.merchantId = settings.google_pay_merchant_id;
    }

    let button_clicked = false;

    // initialize the hosted iframes using the SDK setup function
    init_paysafe_fields(settings, paysafeOptions, current_cart_data, true)
        .then( instance => {
            saveListenerControllerGooglePay?.abort();
            saveListenerControllerGooglePay = new AbortController();

            document.getElementById('paysafe-google-pay').addEventListener(
                'click',
                function (event) {
                    event.preventDefault();
                    event.stopPropagation();

                    // disable the express checkout block,
                    // payment process started
                    onClick();
                    button_clicked = true;

                    handle_express_payment_click(
                        'GOOGLEPAY',
                        settings,
                        current_cart_data,
                        instance,
                        onError,
                        onClose
                    );
                },
                { signal: saveListenerControllerGooglePay.signal },
            );
        })
        .catch(error => {
            // this means that the initialization of the form failed,
            // disable this payment method
            if (parseInt(error.code) === 9023) {
                canExpressPayWithGooglePay = false;
            }

            handle_paysafe_js_setup_error(
                error,
                settings,
                paysafeOptions,
                current_cart_data,
                button_clicked,
                onError,
                onClose
            );
            button_clicked = false;
        });
}

const Content = ( props ) => {
    const { eventRegistration, emitResponse } = props;
    const { onCheckoutSuccess } = eventRegistration;

    useEffect(() => {
        const unsubscribe = onCheckoutSuccess( (response) => {

            const payment_method_details = {
                googlePay: {
                    accountId: settings.account_id,
                    label: 'google_pay',
                    requestBillingAddress: true,
                    requiredBillingContactFields: ['name', 'phone', 'email', 'postalAddress'],
                    requiredShippingContactFields: ['name', 'phone', 'email', 'postalAddress'],
                }
            };
            if (settings.google_pay_issuer_country) {
                payment_method_details.googlePay.country = settings.google_pay_issuer_country;
            }
            if (settings.google_pay_merchant_id) {
                payment_method_details.googlePay.merchantId = settings.google_pay_merchant_id;
            }

            return main_paysafe_checkout_success_handler(
                response,
                current_cart_data,
                settings,
                {
                    displayPaymentMethods: [
                        'googlePay'
                    ],
                    paymentMethodDetails: payment_method_details,
                    settings: {
                        add_customer_when_single_use_token: true,
                        add_billing_when_single_use_token: true,
                    }
                }, false, emitResponse);
        });

        return () => {
            unsubscribe();
        };
    }, [
        emitResponse.responseTypes.ERROR,
        emitResponse.responseTypes.SUCCESS,
        '',
        [],
        onCheckoutSuccess,
    ]);

    return resolve_paysafe_payment_description(settings);
};

const ExpressContent = ( props ) => {
    const { eventRegistration, onClick, onClose, onError } = props;
    const { onCheckoutSuccess } = eventRegistration;

    const { subscribe, select } = window.wp.data;
    const { checkoutStore } = window.wc.wcBlocksData;

    let wasCalculating = select( checkoutStore ).isCalculating();

    useEffect(() => {
        const unsubscribe = subscribe( () => {
            const isCalculating = select( checkoutStore ).isCalculating();

            if ( isCalculating === wasCalculating ) {
                return;
            }

            is_express_google_pay_initialized = !!isCalculating;

            wasCalculating = isCalculating;
        });
    }, [
        [],
        {},
        subscribe,
    ]);

    useEffect(() => {
        init_google_pay_hosted_integration(onClick, onClose, onError);

        const unsubscribe = onCheckoutSuccess( (response) => {
            return response;
        });

        return () => {
            unsubscribe();
        };
    }, [
        [],
        {},
        onCheckoutSuccess,
    ]);

    return resolve_paysafe_express_payment_description(
        settings,
        wp.element.RawHTML({ children: settings.integration_hosted_form })
    );
};

if (!is_hosted_integration) {
    const PaysafeGooglePayPayments = {
        name: paymentMethodId,
        paymentMethodId: paymentMethodId,
        label: get_paysafe_label(settings.title, settings.icon),
        content: <Content/>,
        edit: <Content/>,
        canMakePayment: (cartData => {
            current_cart_data = cartData;

            return true;
        }),
        ariaLabel: __('Paysafe Checkout', 'paysafe-checkout' ),
        supports: {
            features: settings.supports,
        },
    };

    registerPaymentMethod(PaysafeGooglePayPayments);
} else {
    const PaysafeGooglePayExpressPayment = {
        name: paymentMethodId,
        title: 'Google Pay Express Checkout',
        description: settings.description,
        gatewayId: 'paysafe',
        label: get_paysafe_express_label(),
        content: <ExpressContent/>,
        edit: <ExpressContent/>,
        canMakePayment: (cartData => {
            current_cart_data = cartData;
            if (
                canExpressPayWithGooglePay
            ) {
                return !is_apple_pay_google_pay_combo_activated;
            }


            is_express_google_pay_initialized = false;
            return false;
        }),
        paymentMethodId: paymentMethodId,
        supports: {
            features: settings.supports,
        },
    };

    registerExpressPaymentMethod(PaysafeGooglePayExpressPayment);
}
